import React, { FC, Fragment } from 'react';
import { Box, Unstable_Grid2 as Grid } from '@mui/material';

import { DateRange } from 'types/app';
import { useGetEbrIntroductionQuery, useGetEbrOrganicInsightsQuery } from 'api';
import { useDateRange, useNotify, useProtectedClient } from 'hooks';

import { SECTIONS } from './types';
import { NAV_OFFSET } from './constants';
import { COLORS } from './styles';
import Nav from './Nav';
import {
  Advocates,
  Executives,
  Introduction,
  Opening,
  OrganicInsights,
  Shares,
  Takeaways,
} from './Sections';
import moment from 'moment';

const styles = {
  box: {
    marginTop: `${NAV_OFFSET}px`,
  },
  opening: {
    backgroundColor: COLORS.black1,
    minHeight: '100vh',
  },
  introduction: {
    backgroundColor: COLORS.blue1,
    minHeight: '100vh',
  },
  organicInsights: {
    backgroundColor: COLORS.black1,
    minHeight: '100vh',
  },
  shares: {
    backgroundColor: COLORS.blue2,
    minHeight: '100vh',
  },
  advocates: {
    backgroundColor: COLORS.purple1,
    minHeight: '100vh',
  },
  executives: {
    backgroundColor: COLORS.purple2,
    minHeight: '100vh',
  },
  takeawaysAndNextSteps: {
    backgroundColor: COLORS.black2,
    minHeight: '100vh',
  },
} as const;

const AdvocacyIndex: FC = () => {
  const dateRangeOptions = useDateRange({ defaultRange: DateRange.LastXDays, xDays: 365 });
  const client = useProtectedClient();
  const notify = useNotify();
  const { startDate, endDate } = dateRangeOptions;

  const ebrIntroductionQueryOptions = useGetEbrIntroductionQuery(
    {
      start: moment(startDate).unix(),
      end: moment(endDate).unix(),
      client_id: client.id,
    },
    {
      onError: notify.queryError,
    }
  );

  const ebrOrganicInsightsQueryOptions = useGetEbrOrganicInsightsQuery(
    {
      start: moment(startDate).unix(),
      end: moment(endDate).unix(),
      client_id: client.id,
    },
    {
      onError: notify.queryError,
    }
  );

  return (
    <Fragment>
      <Nav />
      <Grid
        container
        id={SECTIONS.opening}
        sx={styles.opening}
        justifyContent="center"
        alignItems="center"
      >
        <Grid xs={11}>
          <Opening range={dateRangeOptions} />
        </Grid>
      </Grid>
      <Grid container id={SECTIONS.introduction} sx={styles.introduction} justifyContent="center">
        <Grid xs={6}>
          <Box sx={styles.box}>
            <Introduction queryOptions={ebrIntroductionQueryOptions} />
          </Box>
        </Grid>
      </Grid>
      <Grid
        container
        id={SECTIONS.organicInsights}
        sx={styles.organicInsights}
        justifyContent="center"
      >
        <Grid xs={6}>
          <Box sx={styles.box}>
            <OrganicInsights queryOptions={ebrOrganicInsightsQueryOptions} />
          </Box>
        </Grid>
      </Grid>
      <Grid container id={SECTIONS.shares} sx={styles.shares} justifyContent="center">
        <Grid xs={6}>
          <Box sx={styles.box}>
            <Shares />
          </Box>
        </Grid>
      </Grid>
      <Grid container id={SECTIONS.advocates} sx={styles.advocates} justifyContent="center">
        <Grid xs={6}>
          <Box sx={styles.box}>
            <Advocates />
          </Box>
        </Grid>
      </Grid>
      <Grid container id={SECTIONS.executives} sx={styles.executives} justifyContent="center">
        <Grid xs={6}>
          <Box sx={styles.box}>
            <Executives />
          </Box>
        </Grid>
      </Grid>
      <Grid
        container
        id={SECTIONS.takeawaysAndNextSteps}
        sx={styles.takeawaysAndNextSteps}
        justifyContent="center"
      >
        <Grid xs={6}>
          <Box sx={styles.box}>
            <Takeaways />
          </Box>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default AdvocacyIndex;
